<template>
  <div class="trip-log-view">
    <TripLogResultsModal
      v-if="submitted"
      :tripId="tripId"
      :failedAttachments="submissionResult?.FailedAttachmentFileNames ?? []"
      @closeModal="onCloseResultsModal"
    />
    <div class="trip-log-view__header-wrapper">
      <header class="page__header--container clearfix">
        <h1 class="page__title--text pull-left">
          Trip Expense Summary
          <small v-if="tripId">TRIP ID: {{ tripId }} </small>
        </h1>
      </header>
      <div class="trip-log-view__navigation">
        <Button
          type="button"
          theme-color="dark"
          @click="onPreviousClicked"
          data-cy="previous-button"
          >Previous</Button
        >
      </div>
    </div>
    <div class="trip-log-view__content">
      <ExpenseSummaryGridView
        v-if="tripLogStore.expenseSummary && mileageRateLoaded"
        :expenseSummary="tripLogStore.expenseSummary"
        :mileageRate="tripLogStore.mileageRate"
      />
      <ExpenseSummaryGridViewSkeleton v-else />

      <div v-if="newTrip" class="trip-visits-header">
        <h2>Visits In Trip</h2>
        <div>
          <strong>Enter RFR Travel Time:</strong> To/In/From Plant in the
          designated fields.
        </div>
        <div>
          <strong>Allocate Expenses:</strong> Allocate Expenses Manually OR
          Click Allocate Expenses Evenly.
        </div>
        <div>
          <small>
            *Allocate Total Expenses (charged to OU plus RFR Out of Pocket
            Costs)</small
          >
        </div>
        <div>
          <strong>Upload Receipts:</strong> Attach all financial documents
          associated with this trip as applicable
        </div>
        <div class="trip-visits-header__actions">
          <Button
            type="button"
            theme-color="dark"
            @click="allocateEvenly"
            data-cy="allocate-evenly-button"
            >Allocate Expenses Evenly</Button
          >
        </div>
      </div>
      <TripLogVisitsGrid
        :tripVisits="tripLogStore.tripVisits"
        :loading="loadingTripVisits"
        :newTrip="newTrip"
      />
      <TripLogAttachmentsView
        v-if="currentTripId"
        :key="uploadKey"
        :tripId="tripId"
        :newTrip="newTrip"
        ref="attachmentsRef"
      />
      <h3>Add Receipts (Required)</h3>
      <FileUpload v-if="newTrip" v-model="uploadedDocs" />
      <h3>General Comments</h3>
      <textarea
        v-model="generalComment"
        class="general-comment"
        rows="5"
        maxlength="500"
        :disabled="!newTrip"
      ></textarea>
      <div v-if="errors.length" class="trip-log-view__errors">
        <div v-for="error in errors" :key="error" class="trip-log-view__error">
          {{ error }}
        </div>
      </div>
      <div v-if="newTrip" class="trip-log-view__actions">
        <LoadingButton
          type="button"
          theme-color="dark"
          :loading="submitting || savingAttachments"
          @click="saveDocumentsAsync"
          :disabled="submitted"
          data-cy="next-button"
          >Save Documents</LoadingButton
        >
        <LoadingButton
          type="button"
          theme-color="primary"
          :loading="submitting || savingAttachments"
          @click="submitTripAsync"
          :disabled="submitted"
          data-cy="next-button"
          >Submit</LoadingButton
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref } from "vue";
import ExpenseSummaryGridView from "./ExpenseSummaryGridView.vue";
import { useTripLogStore } from "@/stores/useTripLogStore";
import TripLogVisitsGrid from "./TripLogVisitsGrid.vue";
import { Button } from "@progress/kendo-vue-buttons";
import TripLogAttachmentsView from "./TripLogAttachmentsView.vue";
import FileUpload from "@/shared/ui/inputs/FileUpload.vue";
import type { UploadFileInfo } from "@progress/kendo-vue-upload";
import {
  getAttachmentFilesAsync,
  validateTotalUploadSize,
} from "@/shared/helpers/fileHelper";
import type { SubmitTripRequest } from "@/Models/Requests/SubmitTripRequest";
import type { TripVisitDisplay } from "@/Domain/TripVisitDisplay";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import type { SharepointUploadAttachmentsResult } from "@/Models/Results/SharepointUploadAttachmentsResult";
import TripLogResultsModal from "./TripLogResultsModal.vue";
import ExpenseSummaryGridViewSkeleton from "./ExpenseSummaryGridViewSkeleton.vue";

const main = useMainStore();
const tripLogStore = useTripLogStore();

const urlParams = new URLSearchParams(window.location.search);
const tripId = ref(Number(urlParams.get("tripId") || "0") ?? 0);
const currentTripId = ref(0);
const loadingTripVisits = ref(true);
const uploadedDocs = ref<UploadFileInfo[]>([]);
const submitting = ref(false);
const savingAttachments = ref(false);
const uploadKey = ref(0);
const errors = ref<string[]>([]);
const generalComment = ref<string>("");
const noReceiptsCap = ref(0);
const attachmentsRef = ref<InstanceType<typeof TripLogAttachmentsView>>();
const submitted = ref(false);
const submissionResult = ref<SharepointUploadAttachmentsResult>();
const mileageRateLoaded = ref(false);

const newTrip = computed(() => {
  return tripId.value == currentTripId.value;
});

const getTripIdAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetTripId", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
    }),
    (data) => {
      currentTripId.value = data;
    },
    "RfrTripLogView.vue",
    "getTripIdAsync"
  );
};

const defaultTimeValues = [0, 0];
const getTripVisitsAsync = async function () {
  try {
    if (!tripId.value) return;
    loadingTripVisits.value = true;
    await handleResponsePromiseAsync(
      api.get("GetTripVisits", {
        tripId: tripId.value,
        newTrip: newTrip.value,
      }),
      (data) => {
        for (const visit of data) {
          visit.AllocationOfTotalExpenses = visit.ExpenseAllocation ?? 0;
          const timeToPlant = visit.TimeToPlant
            ? visit.TimeToPlant.split(":")
            : defaultTimeValues;
          visit.TimeToPlantHours = Number(timeToPlant[0]);
          visit.TimeToPlantMinutes = Number(timeToPlant[1]);
          const timeInPlant = visit.TimeInPlant
            ? visit.TimeInPlant.split(":")
            : defaultTimeValues;
          visit.TimeInPlantHours = Number(timeInPlant[0]);
          visit.TimeInPlantMinutes = Number(timeInPlant[1]);
          const timeToHome = visit.TimeToHome
            ? visit.TimeToHome.split(":")
            : defaultTimeValues;
          visit.TimeToHomeHours = Number(timeToHome[0]);
          visit.TimeToHomeMinutes = Number(timeToHome[1]);
        }
        tripLogStore.tripVisits = data;
      },
      "RfrTripLogView.vue",
      "getTripVisitsAsync"
    );
  } finally {
    loadingTripVisits.value = false;
  }
};

const getTripExpenseSummaryAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetTripExpensesSummary", {
      tripId: tripId.value,
      newTrip: newTrip.value,
    }),
    (data) => {
      tripLogStore.expenseSummary = data;
      generalComment.value = data?.GeneralComment ?? "";
    },
    "RfrTripLogView.vue",
    "getTripExpenseSummaryAsync"
  );
};

const getMileageRateAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetMileageRateForRfr", {}),
    (data) => {
      tripLogStore.mileageRate = data ?? 0;
    },
    "RfrTripLogView.vue",
    "getMileageRateAsync"
  );
  mileageRateLoaded.value = true;
};

const allocateEvenly = function () {
  const totalExpenses = Number(tripLogStore.totalExpenses);
  const totalVisits = tripLogStore.tripVisits!.length;
  const evenlyAllocated = (totalExpenses / totalVisits).toFixed(2);

  tripLogStore.tripVisits!.forEach((visit) => {
    visit.AllocationOfTotalExpenses = Number(evenlyAllocated);
  });
};

const saveDocumentsAsync = async function () {
  let saved = false;
  try {
    if (savingAttachments.value || submitted.value) return;
    savingAttachments.value = true;

    // Validate total upload size
    if (!validateTotalUploadSize(uploadedDocs.value)) {
      return;
    }

    const files = await getAttachmentFilesAsync(uploadedDocs.value);
    if (files.length === 0) {
      errors.value.push("Please upload at least one document.");
      return;
    }

    await handleResponsePromiseAsync(
      api.post("SaveTripFiles", {
        TripId: tripId.value,
        Files: files,
      }),
      () => {
        saved = true;
        uploadedDocs.value = [];
        uploadKey.value++;
      },
      "RfrTripLogView.vue",
      "saveDocumentsAsync"
    );
  } finally {
    savingAttachments.value = false;
  }
  return saved;
};

const submitTripAsync = async function () {
  try {
    if (submitting.value || submitted.value) return;
    submitting.value = true;
    errors.value = [];

    // Validate total upload size
    if (!validateTotalUploadSize(uploadedDocs.value)) {
      return;
    }

    // Validate receipts
    const files = await getAttachmentFilesAsync(uploadedDocs.value);
    const existingFiles = attachmentsRef.value?.existingAttachments ?? [];
    const overCap =
      Number(tripLogStore.totalExpenses) - tripLogStore.mileageAmount >
      noReceiptsCap.value;
    if (overCap && files.length === 0 && existingFiles.length === 0) {
      errors.value.push(
        `Expenses over $${noReceiptsCap.value} (except mileage) require receipts. Attach receipts above.`
      );
    }

    // Validate total allocation
    const totalAllocated =
      tripLogStore.tripVisits?.reduce(
        (acc, visit) => acc + (visit.AllocationOfTotalExpenses ?? 0),
        0
      ) ?? 0;
    if (
      totalAllocated > 0 &&
      Math.abs(Number(tripLogStore.totalExpenses) - totalAllocated) > 0.1
    ) {
      errors.value.push(
        `Your total allocation amount ($${totalAllocated}) does not match the total expenses ($${tripLogStore.totalExpenses}). Please check your allocations amounts and try again.`
      );
      return;
    }

    if (errors.value.length > 0) return;

    const visits = tripLogStore.tripVisits?.map((v: TripVisitDisplay) => {
      return {
        VisitId: v.VisitId,
        TimeToPlantHours: v.TimeToPlantHours,
        TimeToPlantMinutes: v.TimeToPlantMinutes,
        TimeInPlantHours: v.TimeInPlantHours,
        TimeInPlantMinutes: v.TimeInPlantMinutes,
        TimeToHomeHours: v.TimeToHomeHours,
        TimeToHomeMinutes: v.TimeToHomeMinutes,
        Allocation: v.AllocationOfTotalExpenses,
        Comment: v.Comment,
      };
    });

    const submitModel = {
      TripId: tripId.value,
      GeneralComment: generalComment.value,
      Visits: visits,
      Files: files,
    } as SubmitTripRequest;

    await handleResponsePromiseAsync(
      api.post("SubmitTrip", submitModel),
      (data) => {
        if (!data || !data.Success) {
          errors.value.push(
            "There was an error submitting the trip: \n" + data.Message
          );
          return;
        }
        submissionResult.value = data.Data;
        submitted.value = true;
      },
      "RfrTripLogView.vue",
      "submitTripAsync"
    );
  } finally {
    submitting.value = false;
  }
};

const getNoReceiptsCapAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetNoReceiptsCap", {}),
    (data) => {
      noReceiptsCap.value = data;
    },
    "RfrTripLogView.vue",
    "getNoReceiptCapAsync"
  );
};

const onPreviousClicked = function () {
  const urlParams = new URLSearchParams(window.location.search);
  const menuType = urlParams.get("menu") as string;
  const modeType = urlParams.get("type") as string;
  window.location.href = newTrip.value
    ? `EFRTripExpenses?tripID=${tripId.value}&type=${modeType}&menu=${menuType}`
    : `RfrVisitsSearch?type=${modeType}&menu=${menuType}`;
};

const onCloseResultsModal = function () {
  const urlParams = new URLSearchParams(window.location.search);
  const menuType = urlParams.get("menu") as string;
  const modeType = urlParams.get("type") as string;
  window.location.href = `RfrVisitsSearch?type=${modeType}&menu=${menuType}`;
};

onBeforeMount(async () => {
  await getTripIdAsync();
  getTripVisitsAsync();
  getTripExpenseSummaryAsync();
  getMileageRateAsync();
  getNoReceiptsCapAsync();
});
</script>

<style scoped lang="scss">
.trip-log-view {
  padding: 20px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 900px;
  width: 100%;

  &__header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-flow: wrap-reverse;
  }

  &__error {
    background-color: #f8d7da;
    border-color: #f5c2c7;
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 4px;
    font-weight: bold;
    color: red;
    white-space: break-spaces;
  }

  &__navigation {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__actions {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
  }

  .trip-visits-header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .general-comment {
    resize: vertical;
  }
}
</style>
